import { createContext, useCallback, useMemo, useState } from 'react';

import noop from 'lodash/noop';

import { AppNotification } from '../types';
import { BILLING_ERROR_TEXTS, NO_ACCESS_TEXT, NO_BILLING_ACCESS_TEXT } from '../constants';

/**
 * Defaults for the statuses the gateway answers on exports and background sends. Each one
 * says whether it is worth retrying, because that is the only thing the user can decide.
 * 409 is deliberately absent — it always carries a specific server message.
 */
const STATUS_FALLBACKS: Record<number, { message: string; description: string }> = {
  413: {
    message: 'Export too large',
    description: 'Narrow the date range and try again.',
  },
  429: {
    message: 'Already in progress',
    description: 'The same request is already running. Wait for it to finish rather than starting another.',
  },
  503: {
    message: 'Service unavailable',
    description: 'Try again in a few minutes.',
  },
  504: {
    message: 'Still being prepared',
    description: 'This is taking longer than usual. Try again shortly — it usually completes.',
  },
};

const BILLING_URL_PATTERN = /\/v1\/billing\//;

export const getErrorTitle = (error: any): string | undefined => {
  const detail = error?.response?.data?.detail;
  if (typeof detail?.title === 'string') return detail.title;
  if (typeof detail?.message?.title === 'string') return detail.message.title;
  return undefined;
};

export const generateErrorObject = (error: any) => {
  const detail = error?.response?.data?.detail;
  const backendErrorOptions = detail?.options || detail?.message?.title?.options;
  const notification: AppNotification = {
    message: 'Server Error',
    options: { ...DEFAULT_ERROR_OPTIONS },
  };
  // 403 means the request was refused on permissions, not that something broke.
  // Reported here rather than at each call site so any gate we missed on the
  // client still degrades into a message a user can act on. Deliberately no
  // redirect: one background request failing must not yank the user off the
  // page they are working on.
  if (error?.response?.status === 403) {
    notification.message = 'No access';
    notification.description = NO_ACCESS_TEXT;
  }
  // Gateway statuses that carry no useful body of their own. Set before the detail branches
  // below, so a message the server does send still wins — 409 in particular is shown verbatim.
  const statusFallback = STATUS_FALLBACKS[error?.response?.status];
  if (statusFallback) {
    notification.message = statusFallback.message;
    notification.description = statusFallback.description;
  }
  if (typeof detail === 'string') {
    notification.message = detail;
  }
  if (backendErrorOptions?.variant === 'maintenance' && window?.location) {
    const origin = window.location.origin;
    window.location.assign(`${origin}/#/maintenance`);
  }
  if (detail?.message) {
    notification.message =
      typeof detail.message?.title === 'string'
        ? detail.message?.title
        : detail.message?.title?.message?.title || '';
    notification.description =
      detail.message?.description || detail.message?.title?.description || detail?.description || '';
    notification.options.anchorOrigin = backendErrorOptions?.anchorOrigin;
    notification.options.permanent = !!backendErrorOptions?.permanent;
    notification.options.action = backendErrorOptions?.action;
  } else if (typeof detail?.description === 'string') {
    // The gateway's flat error shape: detail is { description } (optionally { title }) with no message key.
    if (typeof detail?.title === 'string') {
      notification.message = detail.title;
    }
    notification.description = detail.description;
  } else if (typeof detail?.title === 'string') {
    notification.message = detail.title;
  }
  // Billing answers in its own words: the gateway's 403 names billing, and known titles get plain text.
  if (BILLING_URL_PATTERN.test(error?.config?.url || '')) {
    const billingText = BILLING_ERROR_TEXTS[getErrorTitle(error) || ''];
    if (error?.response?.status === 403) {
      notification.message = 'No access';
      notification.description = NO_BILLING_ACCESS_TEXT;
    } else if (billingText) {
      notification.message = billingText.message;
      notification.description = billingText.description || '';
    }
  }
  return notification;
};

const DEFAULT_ERROR_OPTIONS: any = {
  permanent: false,
  variant: 'error',
};

interface Context {
  showNotification: (notification: AppNotification) => void;
  hideNotification: () => void;
  handleServerError: (e: any) => void;
  notification: AppNotification | null;
}

export const NotificationContext = createContext<Context>({
  showNotification: noop,
  hideNotification: noop,
  handleServerError: noop,
  notification: null,
});

const NotificationProvider = ({ children }: { children: any }) => {
  const [notification, setNotification] = useState<AppNotification | null>(null);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const showNotification = useCallback((notification: AppNotification) => {
    setNotification(notification);
  }, []);

  const handleServerError = useCallback((error: any) => {
    console.error(error);
    const notification = generateErrorObject(error);
    setNotification(notification);
  }, []);

  const value = useMemo(
    () => ({
      showNotification,
      hideNotification,
      notification,
      handleServerError,
    }),
    [showNotification, hideNotification, notification, handleServerError]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationProvider;
